"use client";

import { formatDateTimeLocal } from "@/shared/lib/date-time";
import { CompactNumber } from "@/shared/ui/CompactNumber";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";

import type { WalletTransactionRow } from "../../../domain/repo/get-wallet-transactions";

type WalletReportsTableProps = {
  data: WalletTransactionRow[];
  isLoading?: boolean;
  totalCount: number;
  limit: number;
  offset: number;
  onPaginationChange: (offset: number, limit: number) => void;
};

export function WalletReportsTable({
  data,
  isLoading,
  totalCount,
  limit,
  offset,
  onPaginationChange,
}: WalletReportsTableProps) {
  const getTypeLabel = (type: string) => {
    switch (type) {
      case "TOPUP":
        return <Badge variant="default">ເຕີມເງິນ</Badge>;
      case "PAYMENT":
        return <Badge variant="warning">ຈ່າຍ</Badge>;
      case "REFUND":
        return <Badge variant="destructive">ຄືນເງິນ</Badge>;
      case "ADJUSTMENT":
        return <Badge variant="secondary">ປັບປຸງ</Badge>;
      default:
        return <Badge variant="default">{type}</Badge>;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "PENDING":
        return "ລໍຖ້າ";
      case "APPROVED":
        return "ອະນຸມັດ";
      case "POSTED":
        return "ບັນທຶກແລ້ວ";
      case "REJECTED":
        return "ປະຕິເສດ";
      default:
        return status;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "PENDING":
        return "text-yellow-600";
      case "APPROVED":
        return "text-blue-600";
      case "POSTED":
        return "text-green-600";
      case "REJECTED":
        return "text-red-600";
      default:
        return "";
    }
  };

  const totalPages = Math.ceil(totalCount / limit);
  const currentPage = Math.floor(offset / limit) + 1;

  if (isLoading) {
    return (
      <Card className="rounded-xl border bg-card">
        <CardContent className="p-6">
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-12 animate-pulse rounded bg-muted" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="rounded-xl border bg-card p-0">
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="px-4 py-3 text-left font-medium text-sm">ວັນທີ</th>
                <th className="px-4 py-3 text-left font-medium text-sm">
                  ປະເພດ
                </th>
                <th className="px-4 py-3 text-right font-medium text-sm">
                  ຈຳນວນເງິນ (LAK)
                </th>
                <th className="px-4 py-3 text-left font-medium text-sm">
                  ລູກຄ້າ
                </th>
                <th className="px-4 py-3 text-left font-medium text-sm">
                  ສະຖານະ
                </th>
                <th className="px-4 py-3 text-left font-medium text-sm">ອໍເດີ</th>
              </tr>
            </thead>
            <tbody>
              {data.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-8 text-center text-muted-foreground"
                  >
                    ບໍ່ມີຂໍ້ມູນສຳລັບການກອງທີ່ເລືອກ
                  </td>
                </tr>
              ) : (
                data.map((item) => (
                  <tr key={item.id} className="border-b hover:bg-muted/50">
                    <td className="px-4 py-3 text-sm">
                      {formatDateTimeLocal(item.createdAt)}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {getTypeLabel(item.type)}
                    </td>
                    <td className="px-4 py-3 text-right text-sm tabular-nums">
                      <CompactNumber value={item.amountLak} />
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {item.customerName || item.customerPhone || "–"}
                    </td>
                    <td
                      className={`px-4 py-3 text-sm ${getStatusColor(item.status)}`}
                    >
                      {getStatusLabel(item.status)}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {item.refOrderId ? (
                        <span className="font-mono text-xs">
                          {item.refOrderId.slice(0, 8)}...
                        </span>
                      ) : (
                        "–"
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalCount > 0 && (
          <div className="flex items-center justify-between border-t px-4 py-3">
            <div className="text-muted-foreground text-sm">
              ສະແດງ {offset + 1} - {Math.min(offset + limit, totalCount)} ຈາກ{" "}
              {totalCount.toLocaleString("lo-LA")} ລາຍການ
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() =>
                  onPaginationChange(Math.max(0, offset - limit), limit)
                }
                disabled={offset === 0}
                className="rounded-md border px-3 py-1 text-sm transition-colors hover:bg-muted disabled:opacity-50"
              >
                ກ່ອນໜ້າ
              </button>
              <span className="text-muted-foreground text-sm">
                ໜ້າ {currentPage} ຈາກ {totalPages}
              </span>
              <button
                type="button"
                onClick={() =>
                  onPaginationChange(
                    Math.min(totalCount - limit, offset + limit),
                    limit,
                  )
                }
                disabled={offset + limit >= totalCount}
                className="rounded-md border px-3 py-1 text-sm transition-colors hover:bg-muted disabled:opacity-50"
              >
                ຖັດໄປ
              </button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
